import React, { useMemo } from 'react';
import { Check, X } from 'lucide-react';

interface PasswordStrengthIndicatorProps {
  password?: string;
}

export const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({ password = '' }) => {
  const criteria = useMemo(() => [
    { label: 'At least 8 characters', isMet: password.length >= 8 },
    { label: 'Contains uppercase letter', isMet: /[A-Z]/.test(password) },
    { label: 'Contains lowercase letter', isMet: /[a-z]/.test(password) },
    { label: 'Contains a number', isMet: /\d/.test(password) },
    { label: 'Contains a special character', isMet: /[^A-Za-z0-9]/.test(password) }
  ], [password]);

  const metCount = criteria.filter(c => c.isMet).length;
  const strengthPercentage = (metCount / criteria.length) * 100;

  let strengthLabel = 'Very Weak';
  let barColor = 'bg-red-500';

  if (metCount === 5) {
    strengthLabel = 'Strong';
    barColor = 'bg-emerald-500';
  } else if (metCount >= 3) {
    strengthLabel = 'Medium';
    barColor = 'bg-amber-500';
  } else if (metCount >= 1) {
    strengthLabel = 'Weak';
    barColor = 'bg-red-400';
  } else {
    barColor = 'bg-gray-200 dark:bg-gray-700';
  }

  // If password is empty, don't show the colored bar
  if (!password) {
    barColor = 'bg-gray-200 dark:bg-gray-700';
  }

  return (
    <div className="w-full space-y-3 mt-3 transition-all duration-300 ease-in-out">
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          Password Strength
        </span>
        {password && (
          <span className={`text-xs font-bold ${
            metCount === 5 ? 'text-emerald-500' : 
            metCount >= 3 ? 'text-amber-500' : 
            'text-red-500'
          }`}>
            {strengthLabel}
          </span>
        )}
      </div>

      <div className="h-1.5 w-full bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden flex gap-1">
        {[1, 2, 3, 4, 5].map((section) => (
          <div 
            key={section} 
            className={`h-full flex-1 rounded-full transition-all duration-500 ease-out ${
              password && section <= metCount ? barColor : 'bg-transparent'
            }`} 
          />
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-800">
        {criteria.map((criterion, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <div className={`flex items-center justify-center w-4 h-4 rounded-full transition-colors duration-300 ${
              criterion.isMet 
                ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' 
                : 'bg-gray-200 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
            }`}>
              {criterion.isMet ? (
                <Check size={10} strokeWidth={3} />
              ) : (
                <X size={10} strokeWidth={2} />
              )}
            </div>
            <span className={`text-xs transition-colors duration-300 ${
              criterion.isMet 
                ? 'text-gray-700 dark:text-gray-300 font-medium' 
                : 'text-gray-400 dark:text-gray-500'
            }`}>
              {criterion.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
